export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';
export type StoredThemeMode = 'SYSTEM' | 'LIGHT' | 'DARK';

export type ThemeChangeDetail = {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
};

export const THEME_STORAGE_KEY = 'JIDOKA_THEME_MODE';

// The nav can be used before the asynchronous auth/profile bootstrap has
// completed. Keep that selection until a UID is available instead of silently
// losing the remote write.
let pendingRemoteThemeMode: ThemeMode | null = null;
let pendingRemoteThemeUid: string | null = null;

const THEME_COLOR_LIGHT = '#082137';
const THEME_COLOR_DARK = '#041525';
const VALID_THEME_MODES: ThemeMode[] = ['system', 'light', 'dark'];

let currentMode: ThemeMode = 'system';
let currentResolvedTheme: ResolvedTheme = 'light';
let mediaQuery: MediaQueryList | null = null;
let remoteSyncUid: string | null = null;
const subscribers = new Set<(detail: ThemeChangeDetail) => void>();

function isThemeMode(value: string | null): value is ThemeMode {
  return value !== null && VALID_THEME_MODES.includes(value as ThemeMode);
}

function normalizeThemeMode(value: unknown): ThemeMode | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return isThemeMode(normalized) ? normalized : null;
}

function toStoredThemeMode(mode: ThemeMode): StoredThemeMode {
  return mode.toUpperCase() as StoredThemeMode;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {
    return getSystemTheme();
  }

  return mode;
}

function readStoredThemeMode(): ThemeMode {
  try {
    return normalizeThemeMode(window.localStorage.getItem(THEME_STORAGE_KEY)) || 'system';
  } catch (error) {
    console.warn('[Theme] Unable to read theme preference:', error);
    return 'system';
  }
}

function writeStoredThemeMode(mode: ThemeMode) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('[Theme] Unable to save theme preference:', error);
  }
}

function writeBootstrapThemeMode(mode: ThemeMode) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // First-paint theme fallback should never block startup.
  }
}

function updateThemeColor(resolvedTheme: ResolvedTheme) {
  let themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.name = 'theme-color';
    document.head.appendChild(themeColorMeta);
  }

  themeColorMeta.content = resolvedTheme === 'dark' ? THEME_COLOR_DARK : THEME_COLOR_LIGHT;
}

function publishThemeChange(detail: ThemeChangeDetail) {
  subscribers.forEach(listener => listener(detail));
  window.dispatchEvent(new CustomEvent<ThemeChangeDetail>('jidoka:themechange', { detail }));
}

function applyTheme(mode: ThemeMode, options: { persist?: boolean; publish?: boolean } = {}) {
  const resolvedTheme = resolveTheme(mode);
  const changed = mode !== currentMode || resolvedTheme !== currentResolvedTheme;

  currentMode = mode;
  currentResolvedTheme = resolvedTheme;

  document.documentElement.dataset.theme = resolvedTheme;
  document.documentElement.dataset.themeMode = mode;
  updateThemeColor(resolvedTheme);

  if (options.persist) {
    writeStoredThemeMode(mode);
  }

  if (changed && options.publish !== false) {
    publishThemeChange({ mode, resolvedTheme });
  }
}

function handleSystemThemeChange() {
  if (currentMode === 'system') {
    applyTheme('system', { publish: true });
  }
}

function bindSystemThemeListener() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return;
  }

  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return;
  }

  mediaQuery.addListener(handleSystemThemeChange);
}

export function initTheme() {
  currentMode = readStoredThemeMode();
  currentResolvedTheme = resolveTheme(currentMode);
  applyTheme(currentMode, { publish: false });
  bindSystemThemeListener();
}

export function bootstrapTheme() {
  const mode = readStoredThemeMode();
  const resolvedTheme = resolveTheme(mode);

  currentMode = mode;
  currentResolvedTheme = resolvedTheme;

  document.documentElement.dataset.theme = resolvedTheme;
  document.documentElement.dataset.themeMode = mode;
  updateThemeColor(resolvedTheme);
}

export function getThemeMode(): ThemeMode {
  return currentMode;
}

export function getResolvedTheme(): ResolvedTheme {
  return currentResolvedTheme;
}

export function setThemeMode(mode: ThemeMode, options: { persistRemote?: boolean } = {}): void {
  applyTheme(mode, { persist: true, publish: true });

  if (options.persistRemote !== false) {
    pendingRemoteThemeMode = mode;
    pendingRemoteThemeUid = remoteSyncUid;
    void writeRemoteThemePreference(mode);
  }
}

export function subscribeTheme(listener: (detail: ThemeChangeDetail) => void) {
  subscribers.add(listener);
  listener({ mode: currentMode, resolvedTheme: currentResolvedTheme });

  return () => {
    subscribers.delete(listener);
  };
}

async function writeRemoteThemePreference(mode: ThemeMode) {
  // Prefer the authoritative Firebase identity when the profile bootstrap has
  // not called syncThemePreferenceForUser yet. This covers an immediate theme
  // selection from the avatar menu after sign-in.
  try {
    const { auth } = await import('../firebase/client');
    // Never reuse a UID left over from a previous account/session.
    remoteSyncUid = auth.currentUser?.uid || null;
  } catch {
    // The pending selection will be retried by syncThemePreferenceForUser.
  }

  if (!remoteSyncUid) {
    return;
  }

  try {
    const { updateCurrentUserPreferences } = await import('../firebase/Auth');
    await updateCurrentUserPreferences({ theme: toStoredThemeMode(mode) });

    if (pendingRemoteThemeMode === mode) {
      pendingRemoteThemeMode = null;
      pendingRemoteThemeUid = null;
    }

  } catch (error) {
    console.warn('[Theme] Unable to save remote theme preference:', error);
  }
}

export async function syncThemePreferenceForUser(uid: string) {
  remoteSyncUid = uid;

  // Do not carry a selection made by a previous account into this account.
  if (pendingRemoteThemeUid && pendingRemoteThemeUid !== uid) {
    pendingRemoteThemeMode = null;
    pendingRemoteThemeUid = null;
  }

  try {
    const { getCurrentUser } = await import('../firebase/Auth');
    const cachedUser = getCurrentUser();
    const remoteMode = cachedUser?.uid === uid
      ? normalizeThemeMode(cachedUser.preferences?.theme)
      : null;

    // A user may select a theme before this initial profile read completes.
    // Their explicit selection wins and is persisted once the UID is known.
    if (pendingRemoteThemeMode) {
      const pendingMode = pendingRemoteThemeMode;
      applyTheme(pendingMode, { persist: true, publish: true });
      await writeRemoteThemePreference(pendingMode);
      return;
    }

    if (!remoteMode) {
      await writeRemoteThemePreference(currentMode);
      return;
    }

    if (remoteMode !== currentMode) {
      writeBootstrapThemeMode(remoteMode);
      setThemeMode(remoteMode, { persistRemote: false });
    }
  } catch (error) {
    console.warn('[Theme] Unable to load remote theme preference:', error);
  }
}
